import type { Theme } from '@mui/material/styles'
import {
  BOTTOM_NAV_HEIGHT,
  MINI_SESSION_BAR_HEIGHT,
} from './layoutConstants'

/** Estimated visible height for a single-line offline alert in the bottom stack. */
export const OFFLINE_STATUS_BAR_ESTIMATE_PX = 56
/** Compact wake banner height at 375px viewport. */
export const BACKEND_WAKE_BANNER_HEIGHT_PX = 72

export type ShellChromeFlags = {
  bottomNav: boolean
  miniSessionBar: boolean
}

export type ShellBottomOffsetOpts = {
  /** Extra px for layers stacked above nav + mini bar (offline/wake banners, snackbar gap). */
  extraPx?: number
  /** iOS visualViewport keyboard overlap; when > 0, skip safe-area (home indicator is gone). */
  keyboardInsetPx?: number
}

/** Z-order for fixed shell chrome (additive; do not reorder bottom nav without coordinating 8-5). */
export function shellOverlayZIndex(theme: Theme) {
  const base = theme.zIndex.appBar
  return {
    bottomNav: base,
    miniSessionBar: base + 1,
    offlineStatusBar: base + 2,
    backendWakeBanner: base + 3,
    snackbar: theme.zIndex.snackbar,
  }
}

function chromeHeightPx(flags: ShellChromeFlags): number {
  return (flags.bottomNav ? BOTTOM_NAV_HEIGHT : 0) + (flags.miniSessionBar ? MINI_SESSION_BAR_HEIGHT : 0)
}

/** CSS `bottom` offset for a fixed layer sitting above nav + optional mini bar (+ extras). */
export function shellFixedBottomOffsetCss(
  flags: ShellChromeFlags,
  opts: ShellBottomOffsetOpts = {},
): string {
  const extra = Math.max(0, opts.extraPx ?? 0)
  const keyboard = Math.max(0, opts.keyboardInsetPx ?? 0)
  const total = chromeHeightPx(flags) + extra + keyboard
  if (keyboard > 0) {
    return `${total}px`
  }
  if (total === 0) {
    return 'env(safe-area-inset-bottom, 0px)'
  }
  return `calc(${total}px + env(safe-area-inset-bottom, 0px))`
}

/** Estimated height of wake + offline banners when both are visible (stacked, not overlapping). */
export function shellBannerStackEstimatePx(opts: {
  offlineVisible?: boolean
  wakeVisible?: boolean
}): number {
  let extra = 0
  if (opts.wakeVisible) extra += BACKEND_WAKE_BANNER_HEIGHT_PX
  if (opts.offlineVisible) extra += OFFLINE_STATUS_BAR_ESTIMATE_PX
  return extra
}

/** Extra bottom padding for scrollable page content when fixed offline/wake banners are visible. */
export function shellBannerInsetPx(
  flags: ShellChromeFlags,
  opts: { offlineVisible?: boolean; wakeVisible?: boolean },
): number {
  return chromeHeightPx(flags) + shellBannerStackEstimatePx(opts)
}
